import React, { useState, useMemo } from "react";
import { Globe, ShieldCheck, Copy, Check } from "lucide-react";
import type { OrgStats } from "../types";

interface CompactJoinLinkWidgetProps {
  slug: string;
  stats: OrgStats;
}

export const CompactJoinLinkWidget: React.FC<CompactJoinLinkWidgetProps> = ({ slug, stats }) => {
  const [copiedLink, setCopiedLink] = useState(false);

  const joinLinkUrl = useMemo(() => {
    if (!stats?.join_token) return "";
    return `${window.location.origin}/join/${slug}?token=${stats.join_token}`;
  }, [slug, stats?.join_token]);

  const handleCopyLink = () => {
    if (!joinLinkUrl) return;
    void navigator.clipboard.writeText(joinLinkUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  if (!stats.join_link_enabled || !stats.join_token) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-indigo-50/80 via-white to-purple-50/80 border border-indigo-100 rounded-3xl p-6 sm:p-7 shadow-xs relative overflow-hidden">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100/80 border border-indigo-200 text-indigo-700 rounded-full text-xs font-semibold mb-2.5">
            <Globe className="w-3.5 h-3.5" />
            Shareable Employee Join Link
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">
            Onboard {stats.organization_name} Employees
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
            Share this link with your team. Employees signing in with an authorized corporate email will complete a quick skill profiling prompt and enter your workspace automatically.
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-3.5">
            <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Domain Restricted:
            </span>
            {stats.allowed_domains && stats.allowed_domains.length > 0 ? (
              stats.allowed_domains.map((domain) => (
                <span
                  key={domain}
                  className="px-2.5 py-0.5 bg-white border border-slate-200 text-indigo-700 text-xs font-mono rounded-lg shadow-xs"
                >
                  @{domain}
                </span>
              ))
            ) : (
              <span className="text-xs text-amber-700 italic">No domain restriction (Open Link)</span>
            )}
          </div>
        </div>

        <div className="flex-1 max-w-md w-full bg-white border border-slate-200 rounded-2xl p-2 sm:p-2.5 flex items-center gap-2 shadow-xs">
          <input
            type="text"
            readOnly
            value={joinLinkUrl}
            className="w-full bg-transparent px-3 py-1.5 text-xs text-slate-900 font-mono focus:outline-none select-all truncate"
          />
          <button
            onClick={handleCopyLink}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-xs flex-shrink-0 transition active:scale-95 cursor-pointer"
          >
            {copiedLink ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-300" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy Link
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
